import { CaseUser } from "./caseUser"
import { DataType } from "./dataType"
import { Operator } from "./insights/operators"

interface CaseColumn {
	key: string;
	label: string;
	description?: string | null;
	dataType: string;
	comparisonType?: DataType;
	fieldDataType?: DataType;
	rounding?: string;

	filterKey?: string;
	displayKey?: string;
	columnValues?: (api: any) => Promise<any[]>;

	listLabel?: string;
	listItemType?: string;

	sortable?: boolean;
	value?: any;
	valueObject? :any;
	valueLabel?: string;

	definitionId?: 	string | null;
	definitionFields?: CaseColumn[];
	dataSourceId?: 	string | null;
	dynamicField: boolean;

	fieldExtensions?: any;
	operators?: Operator[];

	fieldId?: string | null;

	adminOnly?: boolean;

	group?: (record: any, groupByValue: any) => { key: string, label: string, value: any }[]
}

const usersPlusDynamic = (api: any) => {
	return new Promise(async (resolve) => {
		const users = await api.usersGet()
		resolve([
			{
				id: "[Me]",
				name: "Me",
				dynamicValue: true
			},
			...users
		])
	})
}

const cachedUsersPlusDynamic: (users: any[], fetchUsers?: () => Promise<any>) => Promise<any[]> = async (users: any[], fetchUsers?: () => Promise<any>) => {
	let userOptions = users
	if ((!users || users.length === 0) && fetchUsers) {
		userOptions = await fetchUsers()
	}

	return [
		{
			id: "[Me]",
			name: "Me",
			dynamicValue: true
		},
		...userOptions
	]
}

const caseColumns = [
	{
		key: "caseNumber",
		label: "Case #",
		dataType: "text",
		fieldDataType: DataType.string,
		operators: [Operator.Equals, Operator.NotEqual]
	},
	{
		key: "title",
		label: "Title",
		dataType: "text",
		fieldDataType: DataType.string,
		sortable: true,
		operators: [Operator.Contains]
	},
	{
		key: "summary",
		label: "Summary",
		fieldDataType: DataType.string,
		dataType: "text-area"
	},
	{
		key: "templateId",
		label: "Template",
		dataType: "list",
		filterKey: "templateId",
		displayKey: "templateTitle",
		columnValues: (api: any) => api.categories(),
		listLabel: "title",
		sortable: true,
		operators: [Operator.Equals, Operator.NotEqual],
		fieldDataType: DataType.array
	},
	{
		key: "areas",
		label: "Areas",
		dataType: "list-multiple",
		fieldDataType: DataType.arrayMultiple,
		listLabel: "title",
		columnValues: (api: any) => api.areaList(),
		operators: [Operator.Contains, Operator.IsSet, Operator.NotSet]
	},
	{
		key: "created",
		label: "Created",
		dataType: "date-time",
		sortable: true,
		fieldDataType: DataType.dateTime,
		operators: [Operator.GreaterThan, Operator.LessThan, Operator.Equals, Operator.NotEqual, Operator.DateRange]
	},
	{
		key: "lastUpdated",
		label: "Last Updated",
		dataType: "date-time",
		fieldDataType: DataType.dateTime,
		operators: [Operator.GreaterThan, Operator.LessThan, Operator.Equals, Operator.NotEqual, Operator.DateRange],
		sortable: true
	},
	{
		key: "isClosed",
		label: "Closed",
		dataType: "checkbox",
		fieldDataType: DataType.boolean
	},
	{
		key: "closed",
		label: "Closed At",
		dataType: "date-time",
		fieldDataType: DataType.dateTime,
		sortable: true
	},
	{
		key: "openForDays",
		label: "Days Open",
		dataType: "number",
		fieldDataType: DataType.number,
		sortable: true,
		rounding: "floor"
	},
	{
		key: "statusId",
		label: "Status",
		dataType: "list",
		filterKey: "statusId",
		displayKey: "statusTitle",
		listLabel: "title",
		fieldDataType: DataType.array,
		columnValues: (api: any) => api.statuses(),
		sortable: true
	},
	{
		key: "severityId",
		label: "Severity",
		dataType: "list",
		filterKey: "severityId",
		displayKey: "severityTitle",
		listLabel: "title",
		fieldDataType: DataType.array,
		columnValues: (api: any) => api.severities(),
		sortable: true
	},
	{
		key: "isCase",
		label: "Promoted to Case",
		dataType: "checkbox",
		fieldDataType: DataType.boolean,
		sortable: true
	},
	{
		key: "assigneeId",
		label: "Assignee",
		dataType: "user",
		columnValues: usersPlusDynamic,
		comparisonType: DataType.array,
		fieldDataType: DataType.array,
		listLabel: "name",
		operators: [Operator.Equals, Operator.NotEqual, Operator.IsSet, Operator.NotSet],
		group: (record: any, groupByValue: any, cache: { [key:string]: any }) => {
			if (!groupByValue) {
				return [
					{
						key: "none",
						label: "None set",
						value: null
					}
				]
			} else if (typeof groupByValue === "string") {
				const userValue = cache[groupByValue]
					? cache[groupByValue]
					: cache[groupByValue] = record.users.find((u: CaseUser) => u.id === groupByValue)
				return [{
					key: groupByValue,
					label: userValue ? userValue.name : groupByValue,
					value: userValue
				}]
			}

			return []
		}
	},
	{
		key: "users",
		label: "Collaborators",
		dataType: "list-multiple",
		listLabel: "name",
		listItemType: "user",
		columnValues: usersPlusDynamic,
		fieldDataType: DataType.arrayMultiple,
		value: null,
		valueObject: null,
		operators: [Operator.Contains]
	},
	{
		key: "pendingTeams",
		label: "Awaiting Team",
		dataType: "list-multiple",
		fieldDataType: DataType.arrayMultiple,
		columnValues: (api: any) => api.teamsGet(),
		listLabel: "title",
		operators: [Operator.Contains]
	},
	{
		key: "tags",
		label: "Tags",
		dataType: "list-multiple",
		fieldDataType: DataType.arrayMultiple,
		columnValues: (api: any) => api.tagList(),
		listLabel: "title",
		operators: [Operator.Contains]
	},
	{
		key: "hiddenFromInsights",
		label: "Hidden From Insights",
		dataType: "checkbox",
		fieldDataType: DataType.boolean,
		operators: [Operator.IsSet, Operator.NotSet],
		adminOnly: true
	},
	{
		key: "deleted",
		label: "Deleted At",
		dataType: "date-time",
		sortable: true,
		fieldDataType: DataType.dateTime,
		operators: [Operator.GreaterThan, Operator.LessThan, Operator.Equals, Operator.NotEqual, Operator.DateRange, Operator.IsSet, Operator.NotSet]
	}
] as CaseColumn[]

export { CaseColumn, caseColumns, usersPlusDynamic, cachedUsersPlusDynamic }
